/**
 * A joined room: the lobby (phop `useLobby`: roster, invite link, admin
 * settings, your name, Start) until the match starts, then the match screen. Late
 * joiners see a match-in-progress panel.
 */
import { type PeerInfo, Room, useLobby, useRoom } from '@peterddod/phop';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { BUILD_ID } from '@/config';
import { isMatchSettings, type MatchSettings } from '@/game/sim';
import { PLAYER_COLORS } from '@/game/view/palette';
import { lobbyAdmin } from '@/lib/admin';
import { fetchRtcConfig, ICE_REFRESH_MS } from '@/lib/ice';
import { cleanName, displayName, loadOwnName, MAX_NAME_LENGTH, saveOwnName } from '@/lib/names';
import { shareUrl } from '@/lib/share';
import { DownloadingOverlay, useBuildUpdate } from './BuildUpdate';
import { GameVisuals3D } from './GameVisuals3D';
import {
  MatchSettingsPanel,
  PlayerNameField,
  useAdminSettings,
  useAdminStart,
} from './LobbySettings';
import { TitleLayout } from './splash/TitleScreen';

/**
 * What each peer publishes to the room (`<Room metadata>`): its build, and its
 * name (`lib/names.ts`; phop re-sends metadata when it changes, so a rename
 * reaches everyone). Names are cleaned again for display.
 */
type PeerMeta = { build: string; name?: string };

function isPeerMeta(data: unknown): data is PeerMeta {
  if (typeof data !== 'object' || data === null) return false;
  const { build, name } = data as { build?: unknown; name?: unknown };
  if (typeof build !== 'string' || build.length > 64) return false;
  return name === undefined || (typeof name === 'string' && name.length <= MAX_NAME_LENGTH * 4);
}

/**
 * Every name heard this session, by peer id, kept after the peer leaves so
 * a player who drops out mid-match keeps their name on screen.
 */
function useKnownNames(peerInfo: Record<string, PeerInfo<PeerMeta>>): ReadonlyMap<string, string> {
  const known = useRef(new Map<string, string>());
  return useMemo(() => {
    for (const [id, info] of Object.entries(peerInfo)) {
      const name = cleanName(info.metadata?.name ?? '');
      if (name) known.current.set(id, name);
    }
    return new Map(known.current);
  }, [peerInfo]);
}

/**
 * Lobby players who can't play with us: another territile build (a peer on
 * an older build publishes no build id, which counts too), or no hello yet
 * (`pending`). Peers on another phop wire protocol are left out of the room
 * altogether and are in `incompatiblePeers`.
 */
function buildCheck(
  players: string[],
  peerId: string,
  peerInfo: Record<string, PeerInfo<PeerMeta>>
): { mismatched: string[]; pending: string[] } {
  const others = players.filter((p) => p !== peerId);
  return {
    mismatched: others.filter((p) => peerInfo[p] && peerInfo[p].metadata?.build !== BUILD_ID),
    pending: others.filter((p) => !peerInfo[p]),
  };
}

/**
 * Players on another territile build: phop leaves out peers on another wire
 * protocol, and the lobby marks peers publishing another build id, so say why.
 * Players on an older build update themselves (`useBuildUpdate`); `stuck`
 * means a peer is on a newer build than we can get here.
 */
function VersionWarning({ mismatched = 0, stuck }: { mismatched?: number; stuck: boolean }) {
  const { incompatiblePeers } = useRoom();
  const n = incompatiblePeers.length + mismatched;
  if (n === 0 && !stuck) return null;
  return (
    <p className="mt-3 rounded-xl bg-danger-surface px-3 py-2 text-xs text-warn">
      {stuck
        ? "Another player is on a different version of territile, and it isn't available here yet. Try reloading the page later."
        : `${n === 1 ? 'A player' : `${n} players`} in this lobby ${n === 1 ? 'is' : 'are'} on another version of territile. Players on an older version update automatically; anyone who doesn't needs to reload the page.`}
    </p>
  );
}

/** How long "Copied" shows after copying the invite link. */
const COPIED_MS = 2000;

/**
 * The lobby's link (`lib/share.ts`): anyone opening it joins this lobby. Copy
 * puts it on the clipboard; where that's blocked it selects the text instead.
 */
function InviteLink({ roomId }: { roomId: string }) {
  const link = useMemo(() => shareUrl(window.location.href, roomId), [roomId]);
  const input = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = () => {
    // No clipboard API off secure origins (a dev server on the LAN).
    const write = navigator.clipboard?.writeText(link) ?? Promise.reject();
    write.then(
      () => setCopied(true),
      () => input.current?.select()
    );
  };
  return (
    <div className="mt-3 flex min-w-0 items-center gap-1 rounded-full bg-fill py-0.5 pl-2.5 pr-0.5 text-xs">
      <input
        ref={input}
        readOnly
        aria-label="Invite link"
        value={link}
        onFocus={(e) => e.currentTarget.select()}
        className="min-w-0 flex-1 bg-transparent text-fg-muted outline-none"
      />
      <button type="button" onClick={copy} className="btn btn-secondary h-6 px-3 text-xs">
        {copied ? 'Copied' : 'Copy invite link'}
      </button>
    </div>
  );
}

function LobbyPanel({ children, overlay }: { children: ReactNode; overlay: ReactNode }) {
  return (
    <>
      <TitleLayout>
        <div className="panel title-rise w-full max-w-sm rounded-3xl p-4">{children}</div>
      </TitleLayout>
      {overlay}
    </>
  );
}

function RoomInner({
  name,
  onNameChange,
  onMatchChange,
  onLeave,
}: {
  name: string;
  onNameChange: (name: string) => void;
  onMatchChange: (inMatch: boolean) => void;
  onLeave: () => void;
}) {
  const { isConnected, peerId, roomId, peers: joinOrder } = useRoom();
  // phop's lobby: the lowest peer starts, everyone freezes the same roster
  // and the admin's match settings (the start's config), and peers arriving
  // mid-match are told so by the match host.
  const {
    phase,
    players: lobbyPlayers,
    isLobbyHost,
    start,
    match,
    peerInfo,
  } = useLobby<MatchSettings, PeerMeta>('lobby', {
    validateConfig: isMatchSettings,
    validateMetadata: isPeerMeta,
  });
  const inMatch = phase === 'playing' && match !== null;
  const names = useKnownNames(peerInfo);
  // A newer build in the room (lobby, or a match we can't join): get it.
  const peerBuilds = useMemo(
    () =>
      Object.entries(peerInfo)
        .filter(([id]) => id !== peerId)
        .map(([, info]) => info.metadata?.build ?? null),
    [peerInfo, peerId]
  );
  const update = useBuildUpdate(roomId, peerBuilds, phase !== 'playing');
  const overlay = update === 'downloading' ? <DownloadingOverlay /> : null;
  // Lobby players are sorted like the match roster: the first is phop's host.
  const host = lobbyPlayers[0];
  const admin = lobbyAdmin(joinOrder, lobbyPlayers);
  const isAdmin = admin === peerId;
  // Start waits until every player's build is known and matches ours.
  const { mismatched, pending } = buildCheck(lobbyPlayers, peerId, peerInfo);
  const canStart = phase === 'lobby' && mismatched.length === 0 && pending.length === 0;
  // The settings as the admin is choosing them, live on every peer.
  const [settings, setSettings] = useAdminSettings(isAdmin, admin, phase === 'lobby');
  const adminStart = useAdminStart(isAdmin, admin, isLobbyHost, host, canStart, start);

  useEffect(() => {
    onMatchChange(inMatch);
  }, [inMatch, onMatchChange]);

  if (phase === 'in-progress') {
    return (
      <LobbyPanel overlay={overlay}>
        <p className="panel-title">Match in progress</p>
        <p className="mt-1 text-sm text-fg-muted">This lobby's match has already started.</p>
        <VersionWarning stuck={update === 'notice'} />
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onLeave} className="btn btn-primary">
            Back
          </button>
        </div>
      </LobbyPanel>
    );
  }

  if (!inMatch) {
    // Lobby players are sorted like the match roster, so these colours match in-game.
    return (
      <LobbyPanel overlay={overlay}>
        <div className="flex items-baseline justify-between gap-3 px-1">
          <span className="panel-title truncate">{roomId}</span>
          <span className="shrink-0 text-xs text-fg-subtle">
            {isConnected
              ? `${lobbyPlayers.length} player${lobbyPlayers.length === 1 ? '' : 's'}`
              : 'Connecting…'}
          </span>
        </div>
        <InviteLink roomId={roomId} />
        <ul className="mt-3 space-y-0.5">
          {lobbyPlayers.map((player, i) => (
            <li key={player} className="flex items-center gap-2.5 px-1 py-1.5 text-sm">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: PLAYER_COLORS[i % PLAYER_COLORS.length] }}
              />
              <span className="truncate" title={player}>
                {displayName(player, names.get(player))}
              </span>
              <span className="ml-auto shrink-0 text-xs text-fg-subtle">
                {[
                  player === peerId && 'you',
                  player === admin && 'admin',
                  player === host && admin !== host && 'host',
                  mismatched.includes(player) && 'other version',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </li>
          ))}
        </ul>
        <VersionWarning mismatched={mismatched.length} stuck={update === 'notice'} />
        <MatchSettingsPanel
          settings={settings}
          onChange={isAdmin ? setSettings : undefined}
          playerCount={lobbyPlayers.length}
          roomId={roomId}
        />
        <PlayerNameField name={name} onChange={onNameChange} />
        <div className="mt-4 flex items-center justify-between gap-2">
          <button type="button" onClick={onLeave} className="btn btn-ghost">
            Leave
          </button>
          {isAdmin ? (
            <div className="flex items-center gap-2">
              {mismatched.length > 0 ? (
                <span className="text-sm text-fg-subtle">Waiting for the same version…</span>
              ) : pending.length > 0 ? (
                <span className="text-sm text-fg-subtle">Checking versions…</span>
              ) : null}
              <button
                type="button"
                onClick={() => adminStart(settings)}
                disabled={!canStart}
                className="btn btn-primary"
              >
                Start
              </button>
            </div>
          ) : (
            <span className="px-1 text-sm text-fg-subtle">Waiting for the admin to start…</span>
          )}
        </div>
      </LobbyPanel>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black text-fg">
      <GameVisuals3D players={match.players} settings={match.config} names={names} />
    </div>
  );
}

export function RoomGame({
  signallingServerUrl,
  roomId,
  onMatchChange,
  onLeave,
}: {
  signallingServerUrl: string;
  roomId: string;
  /** Whether a match we're in is running (the title backdrop hides then). */
  onMatchChange: (inMatch: boolean) => void;
  onLeave: () => void;
}) {
  // Our name as typed; published cleaned, and saved for next time.
  const [name, setName] = useState(loadOwnName);
  const metadata = useMemo<PeerMeta>(() => {
    const clean = cleanName(name);
    return clean ? { build: BUILD_ID, name: clean } : { build: BUILD_ID };
  }, [name]);
  useEffect(() => {
    const clean = cleanName(name);
    if (clean) saveOwnName(clean);
  }, [name]);
  // ICE servers (TURN for strict NATs) first; `undefined` means phop's default STUN.
  const [rtcConfig, setRtcConfig] = useState<RTCConfiguration | undefined | 'pending'>('pending');
  useEffect(() => {
    let live = true;
    fetchRtcConfig().then((config) => {
      if (live) setRtcConfig(config);
    });
    // TURN credentials expire; phop reads `rtcConfig` for each new peer
    // connection, so swapping it in (no Room remount) keeps new ones working.
    const refresh = setInterval(() => {
      fetchRtcConfig().then((config) => {
        // A failed refresh keeps the current credentials.
        if (live && config) setRtcConfig(config);
      });
    }, ICE_REFRESH_MS);
    return () => {
      live = false;
      clearInterval(refresh);
    };
  }, []);

  if (rtcConfig === 'pending') {
    return (
      <LobbyPanel overlay={null}>
        <div className="flex items-baseline justify-between gap-3 px-1">
          <span className="panel-title truncate">{roomId}</span>
          <span className="shrink-0 text-xs text-fg-subtle">Connecting…</span>
        </div>
      </LobbyPanel>
    );
  }

  return (
    <Room
      signallingServerUrl={signallingServerUrl}
      roomId={roomId}
      rtcConfig={rtcConfig}
      metadata={metadata}
    >
      <RoomInner
        name={name}
        onNameChange={setName}
        onMatchChange={onMatchChange}
        onLeave={onLeave}
      />
    </Room>
  );
}
